import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { AiLimitErrorMessage } from '../aiUsage/AiLimitNotice';
import { useAiUsage } from '../../hooks/useAiUsage';
import { getAiLimitErrorKind } from '../../services/aiUsageService';
import { useTranslation } from '../../i18n/useTranslation';
import { Button } from '../atoms/Button/Button';
import { Heading } from '../atoms/Typography/Heading';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { Alert } from '../atoms/Alert/Alert';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { DoseText, LimitBadge } from './InsightChips';
import { AiFeedbackControl } from '../aiFeedback/AiFeedbackControl';
import { useReefSuggestions } from '../../hooks/useReefInsight';
import {
  useAcceptSuggestion,
  useDismissSuggestion,
  useEditAndAcceptSuggestion,
} from '../../hooks/useAiSuggestions';
import {
  MAX_EDITED_AMOUNT_MULTIPLIER,
  classifyInsightError,
} from '../../services/insightService';
import type { ReefSuggestion } from '../../services/insightService';
import './ReefSuggestionList.css';

interface EditErrors {
  amount?: string;
  frequency?: string;
}

interface SuggestionItemProps {
  suggestion: ReefSuggestion;
  aquariumId: string;
  onDone: (message: string) => void;
}

function SuggestionItem({ suggestion, aquariumId, onDone }: SuggestionItemProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const formId = useId();
  const formTitleId = useId();
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const restoreEditFocus = useRef(false);
  const { acceptSuggestion, isPending: isAccepting } = useAcceptSuggestion();
  const { dismissSuggestion, isPending: isDismissing } = useDismissSuggestion();
  const { editAndAcceptSuggestion, isPending: isSaving } = useEditAndAcceptSuggestion();

  const [isEditing, setIsEditing] = useState(false);
  const [amount, setAmount] = useState(suggestion.suggestedAmount?.toString() ?? '');
  const [frequency, setFrequency] = useState(suggestion.suggestedFrequency ?? '');
  const [errors, setErrors] = useState<EditErrors>({});
  const [actionError, setActionError] = useState<string | null>(null);

  const busy = isAccepting || isDismissing || isSaving;
  const vars = { suggestionId: suggestion.id, aquariumId };

  function handleFailure(err: unknown): void {
    setActionError(
      classifyInsightError(err) === 'conflict'
        ? t('insights.reef.alreadyHandled')
        : t('insights.reef.actionFailed'),
    );
  }

  async function handleAccept(): Promise<void> {
    setActionError(null);
    try {
      await acceptSuggestion(vars);
      onDone(t('insights.reef.taskCreated'));
    } catch (err) {
      handleFailure(err);
    }
  }

  async function handleDismiss(): Promise<void> {
    setActionError(null);
    try {
      await dismissSuggestion(vars);
      onDone(t('insights.reef.dismissed'));
    } catch (err) {
      handleFailure(err);
    }
  }

  function validate(): { errors: EditErrors; amount?: number; frequency: string } {
    const next: EditErrors = {};
    const trimmedFrequency = frequency.trim();
    let parsedAmount: number | undefined;
    if (amount.trim() !== '') {
      parsedAmount = Number(amount);
      const original = suggestion.suggestedAmount;
      if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
        next.amount = t('insights.reef.amountInvalid');
      } else if (original !== null && original > 0 && parsedAmount > original * MAX_EDITED_AMOUNT_MULTIPLIER) {
        next.amount = t('insights.reef.amountTooHigh', { max: original * MAX_EDITED_AMOUNT_MULTIPLIER });
      }
    }
    if (trimmedFrequency === '') {
      next.frequency = t('insights.reef.frequencyRequired');
    }
    return { errors: next, amount: parsedAmount, frequency: trimmedFrequency };
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setActionError(null);
    const result = validate();
    setErrors(result.errors);
    if (result.errors.amount !== undefined || result.errors.frequency !== undefined) return;
    try {
      await editAndAcceptSuggestion({
        ...vars,
        ...(result.amount !== undefined ? { amount: result.amount } : {}),
        frequency: result.frequency,
      });
      onDone(t('insights.reef.taskCreated'));
    } catch (err) {
      handleFailure(err);
    }
  }

  // The action buttons re-mount when the editor closes; return focus to
  // "Edit & create" once they exist again (WCAG 2.4.3).
  useEffect(() => {
    if (!isEditing && restoreEditFocus.current) {
      restoreEditFocus.current = false;
      editButtonRef.current?.focus();
    }
  }, [isEditing]);

  function closeEditor(): void {
    restoreEditFocus.current = true;
    setIsEditing(false);
    setErrors({});
  }

  return (
    <li className="rg-reef-suggestions__item">
      <Stack gap="3">
        <Stack gap="2">
          <Inline justify="between" gap="2">
            <Text as="span" weight="semibold" id={titleId} className="rg-reef-suggestions__title">
              {suggestion.suggestedTitle}
            </Text>
            <LimitBadge element={suggestion.element} advisoryOnly={suggestion.advisoryOnly} />
          </Inline>
          <DoseText
            amount={suggestion.suggestedAmount}
            unit={suggestion.suggestedUnit}
            frequency={suggestion.suggestedFrequency}
            unknownConcentration={suggestion.unknownConcentration}
          />
        </Stack>

        {!isEditing && (
          <Inline gap="2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void handleAccept()}
              isLoading={isAccepting}
              disabled={busy}
              aria-describedby={titleId}
            >
              {t('insights.reef.createTaskAction')}
            </Button>
            <Button
              ref={editButtonRef}
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(true)}
              disabled={busy}
              aria-describedby={titleId}
            >
              {t('insights.reef.editAndCreateAction')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void handleDismiss()}
              isLoading={isDismissing}
              disabled={busy}
              aria-describedby={titleId}
            >
              {t('insights.reef.dismissAction')}
            </Button>
          </Inline>
        )}

        <AiFeedbackControl outputType="ai_task_suggestion" outputId={suggestion.id} aquariumId={aquariumId} />

        {isEditing && (
          <form
            id={formId}
            className="rg-reef-suggestions__form"
            aria-labelledby={formTitleId}
            noValidate
            onSubmit={(event) => void handleSubmit(event)}
          >
            <Stack gap="3">
              <Text as="span" size="sm" weight="semibold" id={formTitleId}>
                {t('insights.reef.editFormTitle')}
              </Text>
              <Input
                id={`${formId}-amount`}
                type="number"
                inputMode="decimal"
                step="any"
                min="0"
                label={t('insights.reef.amountLabel')}
                helperText={suggestion.suggestedUnit ?? undefined}
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                error={errors.amount}
                fullWidth
              />
              <Input
                id={`${formId}-frequency`}
                label={t('insights.reef.frequencyLabel')}
                value={frequency}
                onChange={(event) => setFrequency(event.target.value)}
                error={errors.frequency}
                required
                fullWidth
              />
              <Inline gap="2">
                <Button type="submit" variant="primary" size="sm" isLoading={isSaving} disabled={busy}>
                  {t('insights.reef.saveAndCreateAction')}
                </Button>
                <Button variant="ghost" size="sm" onClick={closeEditor} disabled={isSaving}>
                  {t('insights.reef.cancelAction')}
                </Button>
              </Inline>
            </Stack>
          </form>
        )}

        {actionError !== null && <Alert compact>{actionError}</Alert>}
      </Stack>
    </li>
  );
}

export interface ReefSuggestionListProps {
  aquariumId: string;
}

/**
 * Open AI suggestions for the aquarium with the Create task / Edit & create /
 * Dismiss actions (existing ai-suggestions routes). Tasks are only ever created
 * by these explicit user actions.
 */
export function ReefSuggestionList({ aquariumId }: ReefSuggestionListProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const statusRef = useRef<HTMLElement>(null);
  const [message, setMessage] = useState('');
  const {
    suggestions,
    isLoading,
    error,
    retry,
    generate,
    isGenerating,
    generateError,
  } = useReefSuggestions(aquariumId);
  const { exceeded: aiCreditsUsedUp } = useAiUsage();

  function handleDone(text: string): void {
    setMessage(text);
    // The handled item leaves the list; keep keyboard focus in the card.
    statusRef.current?.focus();
  }

  return (
    <Stack gap="3" as="section" aria-labelledby={headingId}>
      <Heading level={4} id={headingId}>
        {t('insights.reef.suggestionsTitle')}
      </Heading>

      <LiveRegion ref={statusRef} tabIndex={-1} className="rg-reef-suggestions__status">
        {message !== '' && <Text size="sm" color="success">{message}</Text>}
      </LiveRegion>

      {isLoading && (
        <Text size="sm" color="secondary">
          {t('common.loading')}
        </Text>
      )}

      {!isLoading && error != null && (
        <LoadErrorState
          compact
          title={t('insights.reef.suggestionsError')}
          retryLabel={t('insights.reef.retryAction')}
          onRetry={retry}
          live="polite"
        />
      )}

      {!isLoading && error == null && suggestions.length === 0 && (
        <Stack gap="2" align="start">
          <Text size="sm" color="secondary">
            {t('insights.reef.noSuggestions')}
          </Text>
          <Button
            variant="secondary"
            size="sm"
            onClick={generate}
            isLoading={isGenerating}
            disabled={isGenerating || aiCreditsUsedUp}
          >
            {isGenerating ? t('insights.reef.generating') : t('insights.reef.generateAction')}
          </Button>
          <AiLimitErrorMessage error={generateError} suppressExceeded={aiCreditsUsedUp} />
          {generateError != null && getAiLimitErrorKind(generateError) === null && (
            <Alert compact>{t('insights.reef.generateFailed')}</Alert>
          )}
        </Stack>
      )}

      {!isLoading && error == null && suggestions.length > 0 && (
        <ul className="rg-reef-suggestions">
          {suggestions.map((suggestion) => (
            <SuggestionItem
              key={suggestion.id}
              suggestion={suggestion}
              aquariumId={aquariumId}
              onDone={handleDone}
            />
          ))}
        </ul>
      )}
    </Stack>
  );
}

export default ReefSuggestionList;
